// src/pages/Features/Features.jsx (adjust path if yours is different)
import React from "react";
import Navbar from "../../components/Navbar";
import "../../styles/Features.css";
import { trackEvent } from "../../firebase/analytics";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Features = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent("features_page_view", {
      page: "features",
    });
  }, []);

  return (
    <>
      <Navbar />

      <div className="features-page">
        <header className="features-header">
          <h1>Features</h1>
          <p>
            Explore how Oiiko replaces multiple HR tools with one powerful
            platform giving employers clarity, control, and time back in their
            day.
          </p>
        </header>

        <section className="features-list">
          {/* Employee Management */}
          <div className="feature-card employee-management-card">
            <div className="feature-card-text">
              <h2>Employee Management</h2>

              <p className="feature-what">
                Get employees onboarded quickly and keep everything about your
                team organized, accurate, and easy to manage.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>
                  Spend less time onboarding and more time running your
                  business.
                </li>
                <li>Stop chasing employees for information and paperwork.</li>
                <li>
                  Keep employee records consistent, complete, and up to date.
                </li>
                <li>
                  Give managers instant access to the information they need.
                </li>
                <li>Reduce mistakes and confusion as your team grows.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  <strong>Fast Onboarding:</strong> Get new hires set up without
                  delays or back-and-forth.
                </li>
                <li>
                  <strong>Team Setup at Scale:</strong> Bring your whole team in
                  at once without extra effort.
                </li>
                <li>
                  <strong>Employee Self-Setup:</strong> Let employees complete
                  their own information securely.
                </li>
                <li>
                  <strong>Clear Status Control:</strong> Always know who is
                  active, pending, or no longer with the company.
                </li>
              </ul>

              <div className="feature-chip">
                Typical time saved: 2–3 hrs per week
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Employee Management", // change per section
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Time Tracking */}
          <div className="feature-card time-tracking-card">
            <div className="feature-card-text">
              <h2>Time Tracking & Attendance</h2>
              <p className="feature-what">
                Know exactly who is working, when they started, and how many
                hours were actually worked.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Stop chasing employees about missed clock ins.</li>
                <li>Reduce payroll mistakes before they become disputes.</li>
                <li>See attendance clearly without constant check ins.</li>
                <li>Build trust with accurate, consistent time records.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  <strong>Reliable Clock Ins:</strong> Employees know their time
                  is recorded correctly.
                </li>
                <li>
                  <strong>Attendance Awareness:</strong> Quickly see who is on
                  time, late, or absent.
                </li>
                <li>
                  <strong>Work Location Clarity:</strong> Understand where work
                  starts without micromanaging.
                </li>
                <li>
                  <strong>Error Protection:</strong> Fix mistakes confidently
                  with a clear history.
                </li>
                <li>
                  <strong>Payroll Ready Records:</strong> Clean time summaries
                  without extra cleanup.
                </li>
              </ul>

              <div className="feature-chip">
                ~30% fewer missing punches in first month
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Time Tracking",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Scheduling */}
          <div className="feature-card scheduling-card">
            <div className="feature-card-text">
              <h2>Scheduling</h2>
              <p className="feature-what">
                Create schedules that adapt when plans change, without losing
                clarity or control.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Handle last minute changes without confusing your team.</li>
                <li>
                  Give employees clear expectations even when schedules shift.
                </li>
                <li>Keep workloads balanced while staying flexible.</li>
                <li>
                  Adjust coverage quickly without breaking payroll or
                  attendance.
                </li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  <strong>Flexible Weekly Scheduling:</strong> Update shifts
                  easily as needs change.
                </li>
                <li>
                  <strong>Multi Location Support:</strong> Move coverage across
                  job sites without rebuilding schedules.
                </li>
                <li>
                  <strong>Off Days and Holidays:</strong> Time off stays
                  reflected even when schedules change.
                </li>
                <li>
                  <strong>Real Time Updates:</strong> Changes are shared
                  instantly so everyone stays aligned.
                </li>
                <li>
                  <strong>Schedule Guardrails:</strong> Prevent work outside
                  approved hours even during changes.
                </li>
                <li>
                  <strong>Weekly Overview:</strong> See coverage and workload
                  clearly before and after changes.
                </li>
              </ul>

              <div className="feature-chip">
                15–25% fewer schedule confusion incidents
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Scheduling",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Task Manager */}
          <div className="feature-card task-manager-card">
            <div className="feature-card-text">
              <h2>Task Manager</h2>
              <p className="feature-what">
                Assign tasks with due dates, checklists, and proof.
              </p>
              <p className="feature-industries">
                Great for teams in construction, cleaning services, property
                management, warehouses, and field operations.
              </p>
              <div className="feature-industry-chips">
                <span className="industry-chip">Construction</span>
                <span className="industry-chip">Cleaning</span>
                <span className="industry-chip">Property Mgmt</span>
                <span className="industry-chip">Warehouse</span>
                <span className="industry-chip">Field Teams</span>
              </div>
              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Turn “Can you handle this?” into a trackable workflow.</li>
                <li>
                  Reduce missed responsibilities with clear ownership and
                  deadlines.
                </li>
                <li>Standardize repeatable work using checklists.</li>
                <li>Require photo proof when needed for accountability.</li>
                <li>
                  Managers see what’s done, overdue, or stuck without chasing
                  people.
                </li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  <strong>Employee + Job Site Assignment:</strong> Track tasks
                  by person and location.
                </li>
                <li>
                  <strong>Due Dates + Overdue Flags:</strong> Instantly see what
                  needs attention.
                </li>
                <li>
                  <strong>Checklist Tasks:</strong> Break work into steps
                  employees can complete.
                </li>
                <li>
                  <strong>Photo Proof:</strong> Require completion proof before
                  review.
                </li>
                <li>
                  <strong>Review Workflow:</strong> Approve or reject
                  submissions with a reason.
                </li>
              </ul>

              <div className="feature-chip">
                Fewer missed tasks. Cleaner accountability.
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Task manager",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Leave Management */}
          <div className="feature-card leave-management-card">
            <div className="feature-card-text">
              <h2>Leave Management</h2>
              <p className="feature-what">
                Manage time off requests and approvals with full visibility.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>No more buried messages or missing requests.</li>
                <li>Automatic PTO balance deduction on approval.</li>
                <li>Attach supporting documents for faster review.</li>
                <li>Instant calendar updates after approval.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>Full, half-day, or custom time-off options.</li>
                <li>Advanced filters by employee, leave type, or status.</li>
                <li>One-click approval/rejection from dashboard.</li>
                <li>Upload and preview documents for context.</li>
              </ul>

              <div className="feature-chip">Approvals in hours, not days</div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Leave Management",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Expense Management */}
          <div className="feature-card expense-management-card">
            <div className="feature-card-text">
              <h2>Expense Management</h2>
              <p className="feature-what">
                Capture receipts, control reimbursements, and keep budgets
                clear.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>No more missing receipts or spreadsheet hunts.</li>
                <li>Keep spending transparent and accountable.</li>
                <li>Fast approvals keep employees reimbursed promptly.</li>
                <li>Auditable trail for finance and compliance.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>Expense submission with receipts & categories.</li>
                <li>Real-time review queue for Pending, Approved, Rejected.</li>
                <li>Document preview before decision.</li>
                <li>Single-click approval or rejection.</li>
              </ul>

              <div className="feature-chip">
                Approvals within 24–48 hours typical
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Expense Management",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Training Manager */}
          <div className="feature-card training-manager-card">
            <div className="feature-card-text">
              <h2>Training Manager</h2>
              <p className="feature-what">
                Assign courses, track progress, and raise completion rates with
                ease.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Faster onboarding and compliance across teams.</li>
                <li>Visibility into who’s behind—before deadlines slip.</li>
                <li>Central place for all learning materials.</li>
                <li>Clear accountability and completion tracking.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  Video, PDF, or link-based courses with custom deadlines.
                </li>
                <li>Assign to one or many employees at once.</li>
                <li>Track progress (Pending, In-Progress, Completed).</li>
                <li>Highlight overdue or upcoming deadlines automatically.</li>
              </ul>

              <div className="feature-chip">
                25–40% higher on-time completion rate
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Training Manager",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Reports & Analytics */}
          <div className="feature-card reports-analytics-card">
            <div className="feature-card-text">
              <h2>Reports & Analytics</h2>
              <p className="feature-what">
                Turn daily operations into data you can act on instantly.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Spot risks early like overtime or attendance issues.</li>
                <li>
                  Get instant insights across HR, time, leave, and training.
                </li>
                <li>Track growth, efficiency, and team engagement trends.</li>
                <li>Build confidence in every decision with live metrics.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>Employee trends by role, status, and tenure.</li>
                <li>Time analytics: hours, lateness, and top contributors.</li>
                <li>Scheduling coverage and shift utilization.</li>
                <li>Training and leave breakdowns with visual dashboards.</li>
              </ul>

              <hr className="feature-divider" />

              <div className="feature-chip">
                Make data driven decisions in minutes
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Reports & Analytics",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Payroll-Ready Reports */}
          <div className="feature-card payroll-reports-card">
            <div className="feature-card-text">
              <h2>Payroll-Ready Reports</h2>
              <p className="feature-what">
                Clean summaries of hours, overtime, and paid leave you can
                export straight to payroll in a few clicks.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Stop spending weekends reconciling timesheets.</li>
                <li>
                  Make sure overtime and paid leave are captured correctly every
                  period.
                </li>
                <li>
                  Give bookkeepers and accountants clean data—not messy
                  spreadsheets.
                </li>
                <li>
                  Reduce payroll disputes with clear, transparent breakdowns.
                </li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  Pay-period views (weekly, bi-weekly, or monthly) that match
                  how you actually run payroll.
                </li>
                <li>
                  Separate regular, overtime, and paid-leave hours for each
                  employee.
                </li>
                <li>
                  Automatically pulls from approved time, leave, and schedules.
                </li>
                <li>
                  CSV export you can hand straight to your payroll provider or
                  accountant.
                </li>
              </ul>

              <div className="feature-chip">
                Cut payroll prep from hours to minutes
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Payroll reports",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Help Desk */}
          <div className="feature-card helpdesk-card">
            <div className="feature-card-text">
              <h2>Help Desk</h2>
              <p className="feature-what">
                Track, assign, and resolve employee issues with full visibility.
              </p>

              <h4 className="feature-why">Why it matters</h4>
              <ul className="feature-value">
                <li>Faster internal support and fewer follow-ups.</li>
                <li>Clear ownership no ticket gets lost or ignored.</li>
                <li>Improve response times and track SLA performance.</li>
                <li>Build trust through transparency and communication.</li>
              </ul>

              <h4 className="feature-highlights">Highlights</h4>
              <ul className="feature-highlights-list">
                <li>
                  Submit issues with title, description, category, and priority.
                </li>
                <li>Track every ticket (Open → Claimed → Resolved).</li>
                <li>Assign to agents with urgency and attachments.</li>
                <li>Comment and close with context for the requester.</li>
              </ul>

              <hr className="feature-divider" />

              <div className="feature-chip">
                Typical resolution time: 30–50% faster
              </div>

              <div className="feature-footer">
                <button
                  className="feature-mini-cta"
                  onClick={() => {
                    trackEvent("features_cta_click", {
                      feature: "Help desk",
                      location: "feature_card",
                    });

                    navigate("/register");
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Features;
